import { CustomAppEvents } from 'core/common/entities';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { BingAdsEventTypes, BingAdsMethods, AnalyticsPixelId } from '../../entities';
import { BingAdsService } from '../../interfaces';

const noop: BingAdsMethods & { isAvailable: false } = {
  isAvailable: false,
  revokeConsent: () => {},
  grantConsent: () => {},
  pageLoad: () => {},
  track: () => {},
};

export class HttpBingAdsService implements BingAdsService {
  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];

  constructor(
    private readonly cookieConsentService: CookieConsentService,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isUetqRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isUetqAvailable(): boolean {
    return this.uetq.isAvailable;
  }

  track(eventName: BingAdsEventTypes, data: Record<string, unknown>) {
    this.uetq.track(eventName, data);
  }

  private get uetq(): BingAdsMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window.uetq) {
      return noop;
    }

    const uetq = window.uetq;

    return {
      isAvailable: true,
      grantConsent: () => uetq.push('consent', 'update', { ad_storage: 'granted' }),
      revokeConsent: () => uetq.push('consent', 'update', { ad_storage: 'denied' }),
      pageLoad: () => uetq.push('pageLoad'),
      track: (eventName: BingAdsEventTypes, data: Record<string, unknown>) =>
        uetq.push('event', eventName, data),
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
        return;
      }

      this.uetq.revokeConsent();
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
        return;
      }

      this.uetq.revokeConsent();
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.uetq.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.BING_ADS_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    this.uetq.grantConsent();

    pixelsToInitialize.forEach((pixel) => {
      if (!this.initializedPixelIds.includes(pixel)) {
        this.initializedPixelIds.push(pixel);
      }
    });

    this.uetq.pageLoad();
  }
}
